<template>
  <div
    class="microcart cl-accent bg-cl-primary"
    data-testid="microcart"
  >
    <div class="row px20 actions end-xs mx0">
      <div class="col-xs-8 center-xs visible-xs">
        <div @click="closeMicrocartExtend">
          <router-link :to="localizedRoute('/')" :title="$t('Home Page')" class="no-underline inline-flex py15">
            <logo-image/>
          </router-link>
        </div>
      </div>
      <div class="col-xs-2 end-xs">
        <button class="p0 brdr-none bg-cl-transparent close" @click="closeMicrocartExtend"
                type="button"
                data-testid="closeMicrocart"
        >
          <i class="material-icons py20 cl-accent">close</i>
        </button>
      </div>
    </div>
    <div class="row middle-xs bg-cl-primary top-sm px20 actions m0">
      <div class="col-xs-4 col-sm title">
        <h2 v-if="getGroupedByPartnerProducts.length || getCartSkeleton" class="cl-accent mt35 mb35">
          {{ $t('Shopping cart') }}
        </h2>
      </div>
      <div class="col-xs-8 col-sm mt35 mb35 end-sm clearcart-col">
        <clear-cart-button v-if="getGroupedByPartnerProducts.length" @click.native="clearCart" />
      </div>
    </div>
    <div v-show="!getGroupedByPartnerProducts.length && !getCartSkeleton" class="microcart__empty align-center">
      <div class="microcart__empty-message">
        <h4 class="cl-accent">
          {{ $t('Your shopping cart is empty.') }}
        </h4>
        <div class="button-centered">
          <button-full
            class="bg-cl-korzyna-green checkout-link"
            @click.native="returnToShipping"
          >
            {{ $t('Return to shopping') }}
          </button-full>
        </div>
      </div>
      <recent-order-reorder />
    </div>
    <cart-skeleton v-if="getCartSkeleton"/>
    <div v-for="group in getGroupedByPartnerProducts" :key="group.partner.code" class="partner-block" v-if="getGroupedByPartnerProducts.length && !getCartSkeleton">
      <partner-microcart :group="group" />
    </div>
    <div v-if="getGroupedByPartnerProducts.length" class="summary cl-accent serif bg-cl-secondary" >
      <p class="summary-total align-center weight-500 medium">{{ $t('Order amount') }}</p>
      <div v-for="(segment, index) in totals"
           :key="index" class="row middle-xs"
           v-if="segment.code !== 'grand_total' && segment.code === 'subtotal' || segment.code === 'subtotalInclTax'"
      >
        <div class="col-xs inline-flex fs16 weight-700 total-price-label pb10">
          {{ $t('In the amount of') }}
          <button v-if="appliedCoupon && segment.code === 'discount'" type="button" class="p0 brdr-none bg-cl-transparent close delete-button ml10" @click="clearCoupon">
            <i class="material-icons cl-accent">
              close
            </i>
          </button>
          <span class="separator col-xs ml10 bottom"></span>
        </div>
        <div v-if="segment.value != null" class="align-right fs16 total-price-value bold">
          {{ subtotal | price }}
        </div>
      </div>
      <div class="row middle-xs pb20">
        <div class="col-xs inline-flex fs16 total-price-label">
          {{ $t('Delivery') }}
          <span class="separator col-xs ml10 bottom"></span>
        </div>
        <div class="align-right fs16 weight-700 total-price-value bold">
          <order-delivery-price :price="getTotalDeliveryPrice()" @add-address="handleClickAddAddress" />
        </div>
      </div>

      <div class="row  middle-xs pt20 pb10 brdr-top-1 brdr-cl-white">
        <div class="col-xs inline-flex h4 total-price-label bold">
          {{ $t('Total') }}
          <span class="separator col-xs ml10 bottom"></span>
        </div>
        <div class="align-right fs16 weight-700 total-price-value bold">
          {{ orderTotal | price }}
        </div>
      </div>
      <div v-if="availabilityTimeEnabled" class="h4 asap-unavailable-message cl-error bold align-center">
        {{ $t('ASAP shipping is not available due to items in your cart') }}
      </div>
    </div>
    <div
      class="row middle-xs actions actions-bottom m0 bg-cl-secondary"
      v-if="getGroupedByPartnerProducts.length && !isCheckoutMode"
    >
      <div class="col-xs-12 col-sm first-sm link-root">
        <div class="no-underline cl-secondary link pointer">
          <span @click="returnToShipping">
            {{ $t('Return to shopping') }}
          </span>
        </div>
      </div>
      <div class="col-xs-12 first-xs col-sm-6 end-sm" v-if="isCheckoutAllowed">
        <button-full
          class="bg-cl-korzyna-green checkout-link"
          :link="checkoutLink"
          @click.native="closeMicrocartExtend"
          :disabled="isCheckoutDisabled || !enabledPartner"
        >
          {{ $t('Go to checkout') }}
        </button-full>
        <span class="cl-error" v-if="isCheckoutDisabled">
          {{ $t('Order amount in one of partners is not enough to finish checkout') }}
        </span>
        <div class="cl-error center-md center-xs" v-if="!enabledPartner">
          {{ $t('One of the partners is not available') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script async>
import i18n from '@vue-storefront/i18n'

import SearchPanel from '@vue-storefront/core/compatibility/components/blocks/SearchPanel/SearchPanel'
import Microcart from '@vue-storefront/core/compatibility/components/blocks/Microcart/Microcart'
import VueOfflineMixin from 'vue-offline/mixin'
import onEscapePress from '@vue-storefront/core/mixins/onEscapePress'
import Total from '@vue-storefront/core/compatibility/components/blocks/Microcart/Total'

import ClearCartButton from 'theme/components/core/blocks/Microcart/ClearCartButton'
import ButtonFull from 'theme/components/theme/ButtonFull'
import {mapGetters, mapState} from 'vuex'
import PartnerMicrocart from './PartnerMicrocart';
import CartSkeleton from './CartSkeleton';
import LogoImage from '../../LogoImage.vue';
import CartPartnerEligibility, { isPartnerEnabled } from 'theme/mixins/cartPartnerEligibility'

const OrderDeliveryPrice = () => import(/* webpackChunkName: "vsf-order-delivery-price" */ 'theme/components/core/blocks/Checkout/OrderDeliveryPrice.vue');
const RecentOrderReorder = () => import(/* webpackChunkName: "vsf-recent-order-reorder" */ './RecentOrderReorder.vue');

export default {
  components: {
    ClearCartButton,
    ButtonFull,
    PartnerMicrocart,
    CartSkeleton,
    LogoImage,
    OrderDeliveryPrice,
    RecentOrderReorder
  },
  mixins: [
    Microcart,
    VueOfflineMixin,
    onEscapePress,
    SearchPanel,
    Total,
    CartPartnerEligibility
  ],
  data () {
    return {
      addCouponPressed: false,
      couponCode: '',
      componentLoaded: false
    }
  },
  props: {
    isCheckoutMode: {
      type: Boolean,
      required: false,
      default: () => false
    }
  },
  beforeMount () {
    this.$bus.$on('handle-add-address-from-cart', this.handleClickAddAddress);
  },
  beforeDestroy () {
    this.$bus.$off('handle-add-address-from-cart', this.handleClickAddAddress);
  },
  mounted () {
    this.$nextTick(() => {
      this.componentLoaded = true
    })
  },
  computed: {
    ...mapGetters({
      currentStore: 'partners/getCurrentPartner',
      currentLocation: 'locations/getActiveLocation',
      getSeasonalDecoration: 'configs/getSeasonalDecoration',
      getCartSkeleton: 'ui/getCartSkeleton',
      getTotalDeliveryPrice: 'cart/getTotalDeliveryPrice'
    }),
    isCheckoutAllowed () {
      return (this.currentLocation.hasOwnProperty('is_checkout_allowed')) ? this.currentLocation.is_checkout_allowed : true
    },
    orderTotal () {
      const deliveryPrice = this.getTotalDeliveryPrice()
      return deliveryPrice == null ? null : deliveryPrice + this.subtotal
    },
    enabledPartner () {
      return this.getGroupedByPartnerProducts.every(group => isPartnerEnabled(group.partner))
    },
    checkoutLink () {
      let link = { name: 'checkout' },
        locationCode = false;
      if (!this.$route.params.locationCode && this.$store.state.cart.cartItems.length) {
        locationCode = this.$store.state.cart.cartItems[0].location_code
      }
      if (!locationCode && this.currentLocation && this.currentLocation['code']) {
        locationCode = this.currentLocation['code']
      }
      if (locationCode) {
        link.params = {
          locationCode: locationCode
        }
      }
      return link
    },
    returnToShoppingLink () {
      let location = this.$route.params.locationCode
      if (!location && this.$store.getters['locations/getActiveLocation']) {
        location = this.$store.getters['locations/getActiveLocation'].code
      }
      return `/${location}`
    },
    availabilityTimeEnabled () {
      return this.getGroupedByPartnerProducts.some(groupedProducts => {
        return groupedProducts.items.some(item => item.availability_time.availability_time_enable == true);
      })
    }
  },
  methods: {
    addDiscountCoupon () {
      this.addCouponPressed = true
    },
    clearCoupon () {
      this.removeCoupon()
      this.addCouponPressed = false
    },
    async setCoupon () {
      const couponApplied = await this.applyCoupon(this.couponCode)
      this.addCouponPressed = false
      this.couponCode = ''
      if (!couponApplied) {
        this.$store.dispatch('notification/spawnNotification', {
          type: 'warning',
          message: i18n.t("You've entered an incorrect coupon code. Please try again."),
          action1: { label: i18n.t('OK') }
        })
      }
    },
    closeMicrocartExtend () {
      this.closeMicrocart()
      this.$store.commit('ui/setSidebar', false)
      this.addCouponPressed = false
      this.closeSearchpanel()
    },
    returnToShipping () {
      if (typeof this.currentStore === 'undefined') {
        let partnerCode = this.getGroupedByPartnerProducts.length ? this.getGroupedByPartnerProducts[this.getGroupedByPartnerProducts.length - 1].partner.code : ''
        let partnerLink = this.returnToShoppingLink + `/${partnerCode}`
        this.$router.push(partnerLink)
      }
      this.closeMicrocart()
      this.$store.commit('ui/setSidebar', false)
      this.addCouponPressed = false
      this.closeSearchpanel()
    },
    onEscapePress () {
      this.closeMicrocart()
    },
    clearCart () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: i18n.t('Are you sure you would like to remove all the items from the shopping cart?'),
        action1: { label: i18n.t('Cancel'), action: 'close' },
        action2: { label: i18n.t('OK'),
          action: async () => {
            await this.$store.dispatch('cart/clear')
          }
        },
        hasNoTimeout: true
      })
    },
    handleClickAddAddress () {
      this.closeMicrocartExtend();
      this.$bus.$emit('handle-toggle-address-popup');
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~theme/css/animations/transitions";

.microcart {
  display: flex;
  flex-direction: column;
  min-height: 100%;

  &__empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-bottom: 20px;

    &-message {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }
}

.summary {
  margin-top: auto;
}

.minimal-order-amount-notice {
  font-size: 14px;
}

.close {
  i {
    opacity: 0.6;
  }
  &:hover,
  &:focus {
    i {
      opacity: 1;
    }
  }
}

.mt0 {
  @media (max-width: 768px) {
    margin-top: 0;
  }
}

.clearcart {
  &-col {
    display: flex;
    align-self: center;
  }
}

.products {
  @media (max-width: 768px) {
    padding: 30px 15px;
  }
}

.asap-unavailable-message {
  color: #ffa500;
}

.actions {
  padding: 0 20px;

  &-bottom {
    padding: 20px;
  }

  @media (max-width: 768px) {
    padding: 0 12px;

    &-bottom {
      padding: 0 12px 20px;
    }

    .title {
      padding: 0;
    }
  }

  @media only screen and (max-width: 576px) {
    &-bottom {
      padding: 0 0 20px;
    }
  }

  .link {
    @media (max-width: 768px) {
      display: flex;
      justify-content: center;
      padding: 20px 40px;

      &-root {
        padding: 0;
      }

      &.checkout {
        margin-top: 55px;
        padding: 0;
      }
    }

    @media only screen and (max-width: 576px) {
      &-root {
        text-align: center;
        margin-top: 12px;
      }
    }
  }
}

.summary {
  padding: 20px 28px 0;

  @media (max-width: 768px) {
    padding: 20px 20px 0 12px;
    font-size: 12px;
  }
}
.summary-partner{
  @media (max-width: 768px) {
    padding:  0 15px;
  }
}

.summary-heading {
  @media (max-width: 768px) {
    font-size: 18px;
  }
}


.delete-button {
  vertical-align: middle;
}

.coupon-wrapper {
  display: flex;

  .button-outline {
    text-transform: inherit;
    width: 50%;
  }

  .coupon-input {
    margin-right: 20px;
    width: 100%;
  }
}
.bg-cl-korzyna-green{
  border-radius: 10px;
}
.underline:after{
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background-color: #ffffff;
}
.summary-total{
  font-size: 21px;
}
 .separator{
  border-bottom: 1px dashed #fff;
  height: 15px;
   &.bottom{
     border-bottom: 1px dashed rgba(17, 17, 17, 0.11);
   }
}
.total-price-value{
  padding-right: 8px;
  padding-left: 8px;
}
.total-price-label{
  width: calc(100% - 110px);
}
.background{
  background-size: cover;
  background-position: bottom center;
  background-repeat: no-repeat;
}

.row {
  row-gap: 8px;
}
</style>
